import { ErrorMessage } from 'components/Form'
import { useDelayedAction } from 'hooks/useDelayedAction'
import { FocusEventHandler, useCallback, useState } from 'react'
import { Box, Flex } from 'theme-ui'
import { noop } from 'utils'
import { msFromTimeString, timeStringFromMS } from 'utils/helpers'
import { CheckboxInput } from '.'
import { TimeInput } from './TimeInput'

export interface TimeRangeInputProps {
  start: {
    name: string
    label: string
    value?: string | null
    error?: string
  }
  end: {
    name: string
    label: string
    value?: string | null
    error?: string
  }
  defaultRange?: number //default number of milliseconds between start and end times
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  validateForm: () => void
  onBlur?: FocusEventHandler
  disabled?: boolean
  tabIndex: number
}

export const TimeRangeInput = ({
  start,
  end,
  defaultRange = 0,
  setFieldValue,
  onBlur = noop,
  validateForm,
  tabIndex,
  disabled = false,
}: TimeRangeInputProps) => {
  const delayed = useDelayedAction()
  const [range, setRange] = useState<number>(defaultRange)
  const handleSetTime = useCallback(
    (name: string, value: string) => {
      setFieldValue(name, value, false)
      const startMs =
        name === start.name
          ? msFromTimeString(value)
          : msFromTimeString(start.value)
      const endMs =
        name === end.name
          ? msFromTimeString(value)
          : msFromTimeString(end.value)
      if (name === start.name) {
        if (startMs !== undefined) {
          const newEnd = startMs + range
          const endString = timeStringFromMS(newEnd)
          setFieldValue(end.name, endString ?? '', false)
        } else if (startMs === undefined && endMs === undefined) {
          setRange(defaultRange)
        }
      } else if (name === end.name) {
        if (endMs !== undefined) {
          if (startMs !== undefined && startMs <= endMs) {
            setRange(endMs - startMs)
          } else if (startMs === undefined) {
            const newStart = endMs - range
            const startString = timeStringFromMS(newStart)
            setFieldValue(start.name, startString ?? '', false)
          }
        } else if (endMs === undefined) {
          setRange(defaultRange)
        }
      }
      //wait to validate the form until all the field values have been set
      delayed(validateForm)
    },
    [
      setFieldValue,
      defaultRange,
      end.name,
      end.value,
      range,
      start.name,
      start.value,
      validateForm,
      delayed,
    ]
  )
  const handleTimeConfirmedChange = (isTimeConfirmed: boolean) => {
    if (isTimeConfirmed === false) {
      setFieldValue(start.name, '', false)
      setFieldValue(end.name, '', false)
    } else {
      setFieldValue(start.name, null, false)
      setFieldValue(end.name, null, false)
    }
    setRange(defaultRange)
    //wait to validate the form until all the field values have been set
    delayed(validateForm)
  }

  return (
    <>
      <Flex>
        <Box sx={{ pr: 15 }}>
          <TimeInput
            name={start.name}
            label={start.label}
            value={start.value}
            setFieldValue={handleSetTime}
            onBlur={onBlur}
            error={start.error}
            showErrorText={false}
            tabIndex={tabIndex}
            disabled={disabled}
          />
        </Box>
        <Box>
          <TimeInput
            name={end.name}
            label={end.label}
            value={end.value}
            setFieldValue={handleSetTime}
            onBlur={onBlur}
            error={end.error}
            showErrorText={false}
            tabIndex={tabIndex + 0.1}
            disabled={disabled}
          />
        </Box>
      </Flex>
      <Box
        sx={{
          mt: -40,
        }}>
        <CheckboxInput
          label="Not Confirmed"
          value={start.value === '' && end.value === ''}
          name="timeNotConfirmed"
          onBlur={onBlur}
          setFieldValue={(name: any, isNotConfirmed: boolean) => {
            const isTimeConfirmed = !isNotConfirmed
            handleTimeConfirmedChange(isTimeConfirmed)
          }}
          tabIndex={tabIndex + 0.2}
        />
      </Box>
      <ErrorMessage>
        {start.error || end.error ? (
          <span>{start.error ?? end.error}</span>
        ) : null}
      </ErrorMessage>
    </>
  )
}
